import MaterialCommunityIcons from "@expo/vector-icons/MaterialCommunityIcons";
import { useAppTheme } from "@hooks/useAppTheme";
import { Box } from "@memorang/ui";
import { Image } from "expo-image";
import { type ReactNode, useMemo, useState } from "react";
import { Pressable } from "react-native";
import { Caption, Text } from "react-native-paper";
import {
	type ProethicaPrinciple,
	getPrincipleAsset,
	parsePrincipleDescription,
} from "./constants";

type Props = {
	principles: ProethicaPrinciple[];
	// Rendered at the far right of the header row (e.g. a dialog close button).
	headerAction?: ReactNode;
};

const EmphasizedPrincipleTitle = ({
	title,
	color,
	numberOfLines,
	emphasized = false,
}: {
	title: string;
	color: string;
	numberOfLines?: number;
	emphasized?: boolean;
}) => {
	const theme = useAppTheme();
	const firstLetter = title.slice(0, 1);
	const rest = title.slice(1);

	return (
		<Text
			variant="titleSmall"
			numberOfLines={numberOfLines}
			style={{
				color: theme.colors.onSurface,
				fontWeight: emphasized ? "800" : "600",
				lineHeight: 24,
			}}
		>
			<Text
				style={{
					color,
					fontSize: 21,
					lineHeight: 25,
					fontWeight: "900",
				}}
			>
				{firstLetter}
			</Text>
			{rest}
		</Text>
	);
};

const AccordionBody = ({
	principle,
}: {
	principle: ProethicaPrinciple;
}) => {
	const theme = useAppTheme();
	const parsed = parsePrincipleDescription(principle);

	return (
		<Box>
			<Box gap={16} padding={16}>
				<Text
					variant="bodyMedium"
					style={{ color: theme.colors.onSurface, lineHeight: 22 }}
				>
					{parsed.body}
				</Text>
				{parsed.guidingQuestions.length ? (
					<Box gap={10}>
						<Caption
							style={{
								color: theme.colors.primary,
								fontWeight: "800",
								letterSpacing: 0.9,
							}}
						>
							GUIDING QUESTIONS
						</Caption>
						{parsed.guidingQuestions.map((question) => (
							<Box
								key={question}
								flexDirection="row"
								gap={10}
								alignItems="flex-start"
							>
								<Box
									width={6}
									height={6}
									borderRadius={3}
									backgroundColor={theme.colors.primary}
									marginTop={8}
								/>
								<Text
									variant="bodyMedium"
									style={{
										color: theme.colors.primary,
										flex: 1,
										lineHeight: 22,
									}}
								>
									{question}
								</Text>
							</Box>
						))}
					</Box>
				) : null}
			</Box>
		</Box>
	);
};

const AccordionItem = ({
	principle,
	index,
	expanded,
	reviewed,
	isLast,
	onPress,
}: {
	principle: ProethicaPrinciple;
	index: number;
	expanded: boolean;
	reviewed: boolean;
	isLast: boolean;
	onPress: () => void;
}) => {
	const theme = useAppTheme();
	const asset = getPrincipleAsset(principle.title, index);
	const assetColor = theme.dark
		? (asset.darkColor ?? asset.color)
		: asset.color;

	return (
		<Box
			borderBottomWidth={isLast && !expanded ? 0 : 1}
			borderBottomColor={theme.colors.outlineVariant}
		>
			<Pressable
				onPress={onPress}
				accessibilityRole="button"
				accessibilityState={{ expanded }}
				accessibilityLabel={principle.title}
				style={{
					borderLeftWidth: 4,
					borderLeftColor: expanded ? assetColor : "transparent",
					backgroundColor: expanded ? `${assetColor}10` : "transparent",
				}}
			>
				<Box
					flexDirection="row"
					alignItems="center"
					gap={12}
					minHeight={52}
					paddingHorizontal={14}
					paddingVertical={8}
				>
					<Box
						width={38}
						height={38}
						borderRadius={19}
						backgroundColor="#FAF4ED"
						borderWidth={1}
						borderColor={assetColor}
						overflow="hidden"
						alignItems="center"
						justifyContent="center"
					>
						<Image
							source={asset.image}
							contentFit="contain"
							style={{ width: 36, height: 36 }}
							alt=""
						/>
					</Box>
					<Box flex={1}>
						<EmphasizedPrincipleTitle
							title={principle.title}
							color={assetColor}
							numberOfLines={2}
							emphasized={expanded}
						/>
					</Box>
					{reviewed ? null : (
						<Box
							width={8}
							height={8}
							borderRadius={4}
							backgroundColor={theme.colors.error}
						/>
					)}
					<MaterialCommunityIcons
						name={expanded ? "chevron-up" : "chevron-down"}
						size={24}
						color={theme.colors.onSurfaceVariant}
					/>
				</Box>
			</Pressable>
			{expanded ? <AccordionBody principle={principle} /> : null}
		</Box>
	);
};

export const ProethicaPrinciplesExplorer = ({
	principles,
	headerAction,
}: Props) => {
	const theme = useAppTheme();
	const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
	const [reviewed, setReviewed] = useState(() => new Set<number>());

	const reviewedCount = reviewed.size;
	const reviewedLabel = useMemo(
		() => `${reviewedCount} of ${principles.length} reviewed`,
		[reviewedCount, principles.length],
	);
	const reviewedProgress = principles.length
		? Math.round((reviewedCount / principles.length) * 100)
		: 0;

	if (!principles.length) {
		return null;
	}

	return (
		<Box
			borderWidth={1}
			borderColor={theme.colors.outlineVariant}
			overflow="hidden"
			backgroundColor={theme.colors.surface}
		>
			<Box
				flexDirection="row"
				alignItems="center"
				justifyContent="space-between"
				gap={16}
				minHeight={52}
				paddingHorizontal={18}
				paddingVertical={8}
				backgroundColor={theme.colors.surfaceVariant}
				borderBottomWidth={1}
				borderBottomColor={theme.colors.outlineVariant}
			>
				<Caption
					style={{
						letterSpacing: 1,
						color: theme.colors.onSurfaceVariant,
						fontWeight: "800",
					}}
				>
					ETHICAL PRINCIPLES
				</Caption>
				<Box
					flex={1}
					flexDirection="row"
					alignItems="center"
					justifyContent="flex-end"
					gap={12}
				>
					<Box
						flex={1}
						maxWidth={220}
						height={6}
						borderRadius={3}
						backgroundColor={theme.colors.outlineVariant}
						overflow="hidden"
					>
						<Box
							height={6}
							borderRadius={3}
							width={`${reviewedProgress}%`}
							backgroundColor={theme.colors.primary}
						/>
					</Box>
					<Text
						variant="bodySmall"
						style={{
							color: theme.colors.onSurfaceVariant,
							fontWeight: "600",
						}}
					>
						{reviewedLabel}
					</Text>
				</Box>
				{headerAction}
			</Box>
			<Box>
				{principles.map((principle, index) => (
					<AccordionItem
						key={principle.title}
						principle={principle}
						index={index}
						expanded={index === expandedIndex}
						reviewed={reviewed.has(index)}
						isLast={index === principles.length - 1}
						onPress={() => {
							setExpandedIndex((current) => (current === index ? null : index));
							setReviewed((current) => new Set(current).add(index));
						}}
					/>
				))}
			</Box>
		</Box>
	);
};
